/**
 * BudgetDuplicateModal - Modal for duplicating a budget with optional inflation
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DuplicateBudgetRequest, InflationScope } from '@budgira/shared';

interface BudgetDuplicateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: DuplicateBudgetRequest) => Promise<void>;
  sourceBudgetName: string;
  isSubmitting: boolean;
}

export const BudgetDuplicateModal = ({
  isOpen,
  onClose,
  onSubmit,
  sourceBudgetName,
  isSubmitting,
}: BudgetDuplicateModalProps) => {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [applyInflation, setApplyInflation] = useState(false);
  const [inflationRate, setInflationRate] = useState<number>(3);
  const [inflationScope, setInflationScope] = useState<InflationScope>('all');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      // Suggest name based on source
      const year = new Date().getFullYear() + 1;
      setName(`${sourceBudgetName} (${year})`);
      
      // Default to next year
      setStartDate(`${year}-01-01`);
      setEndDate(`${year}-12-31`);
      
      setApplyInflation(false);
      setInflationRate(3);
      setInflationScope('all');
      setErrors({});
    }
  }, [isOpen, sourceBudgetName]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = t('budgets.errors.nameRequired');
    }
    if (!startDate) {
      newErrors.startDate = t('budgets.errors.startDateRequired');
    }
    if (!endDate) {
      newErrors.endDate = t('budgets.errors.endDateRequired');
    }
    if (startDate && endDate && startDate > endDate) {
      newErrors.endDate = t('budgets.errors.endDateBeforeStart');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    const data: DuplicateBudgetRequest = {
      name: name.trim(),
      startDate,
      endDate,
      inflationRate: applyInflation && inflationRate > 0 ? inflationRate : undefined,
      inflationScope: applyInflation ? inflationScope : undefined,
    };

    await onSubmit(data);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-6">
          {t('budgets.duplicateTitle')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('budgets.name')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
            />
            {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
          </div>

          {/* Period */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('budgets.startDate')} *
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              />
              {errors.startDate && (
                <p className="text-sm text-red-600 mt-1">{errors.startDate}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('budgets.endDate')} *
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
              />
              {errors.endDate && (
                <p className="text-sm text-red-600 mt-1">{errors.endDate}</p>
              )}
            </div>
          </div>

          {/* Inflation */}
          <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg space-y-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={applyInflation}
                onChange={(e) => setApplyInflation(e.target.checked)}
                className="text-violet-600 rounded"
              />
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('budgets.applyInflation')}
              </span>
            </label>

            {applyInflation && (
              <>
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">
                    {t('budgets.inflationRate')} (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    max="100"
                    value={inflationRate}
                    onChange={(e) => setInflationRate(parseFloat(e.target.value) || 0)}
                    className="w-24 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-600 dark:text-slate-400 mb-1">
                    {t('budgets.inflationScope')}
                  </label>
                  <select
                    value={inflationScope}
                    onChange={(e) => setInflationScope(e.target.value as InflationScope)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                  >
                    <option value="all">{t('budgets.inflationAll')}</option>
                    <option value="income">{t('budgets.inflationIncome')}</option>
                    <option value="expenses">{t('budgets.inflationExpenses')}</option>
                  </select>
                </div>
              </>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg disabled:opacity-50"
            >
              {isSubmitting ? t('common.saving') : t('budgets.duplicate')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BudgetDuplicateModal;
